import React, { useState, useEffect } from 'react';
import { ChevronUp, Zap, Bot, Code, Shield, Calendar } from 'lucide-react';
import Footer from '../components/Footer';
import ScrollIndicator from '../components/ScrollIndicator';

const PricingCard = ({ title, price, description, features }) => (
  <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300">
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <div className="text-blue-500 text-2xl font-bold mb-4">Starting at ₹{price}/-</div>
    <p className="text-slate-600 mb-6">{description}</p>
    <ul className="space-y-3">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start text-slate-600">
          <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1 mr-3" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Pricing = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowScrollTop(scrollTop > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-container">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white min-h-[90vh] snap-start flex flex-col justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-slate-900/0"></div>
        <div className="container-width relative flex-1 flex flex-col justify-center items-center py-16 sm:py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">Pricing</h1>
            <div className="space-y-4">
              <p className="text-base sm:text-lg text-slate-300">
At ViSRUPT, we craft solutions as unique as your needs. Prices depend on complexity, features, and how ambitious your request is. Here’s a general idea to get you started, but don’t worry, asking for a quote is absolutely free.
              </p>
            </div>
          </div>
        </div>
        <ScrollIndicator text="View Pricing Plans" />
      </section>

      {/* ViSiON Pricing Section */}
      <section className="min-h-screen snap-start bg-slate-50 flex items-center py-24">
        <div className="container-width">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Zap className="w-8 h-8 text-blue-500 mr-3" />
              <h2 className="text-2xl sm:text-3xl font-bold">ViSiON – Algorithmic Trading Solutions</h2>
            </div>
            <p className="text-slate-600 text-lg">
              ViSiON offers precision-driven trading automation to enhance execution speed, minimize manual errors, and maximize opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <PricingCard
              title="For Retail Traders"
              price="7,999"
              description="Custom-built trading strategies for individual traders"
              features={[
                "Rule-based strategies, straddles, strangles",
                "Rollovers and adjustments",
                "Backtesting & optimization services available",
                "Prices vary based on complexity and features required",
                "3-years Dedicated Customer Support"
              ]}
            />
            <PricingCard
              title="For Businesses"
              price="37,999"
              description="Enterprise-grade trading automation and strategy execution"
              features={[
                "Custom integrations, API-driven execution",
                "Risk management tools",
                "Scalable solutions for prop desks, funds, and institutions",
                "Pricing depends on scope, infrastructure, and required optimizations",
                "5-years Dedicated Customer Support"
              ]}
            />
          </div>
        </div>
      </section>

      {/* ViSFLOW Pricing Section */}
      <section className="min-h-screen snap-start bg-white flex items-center py-24">
        <div className="container-width">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Bot className="w-8 h-8 text-blue-500 mr-3" />
              <h2 className="text-2xl sm:text-3xl font-bold">ViSFLOW – Automation & API Development</h2>
            </div>
            <p className="text-slate-600 text-lg">
              ViSFLOW eliminates inefficiencies by automating repetitive tasks, integrating intelligent workflows, and streamlining operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <PricingCard
              title="Chatbots & Automation Bots"
              price="4,999"
              description="Intelligent bots for engagement and automation"
              features={[
                "Telegram & Discord bots for engagement and support",
                "Custom game bots for interactive experiences",
                "Pricing depends on functionalities and integrations" 
              ]}
            />
            <PricingCard
              title="Custom API Development"
              price="45,999"
              description="Tailor-made APIs for seamless operations"
              features={[
                "Scalable and efficient APIs",
                "Built to fit your exact needs",
                "Pricing varies based on complexity and integrations",
                "5-years Dedicated Customer Support"
              ]}
            />
            <div className="md:col-span-2 lg:col-span-1">
              <PricingCard
                title="Other Custom Automation Solutions"
                price="Custom"
                description="Tailored automation solutions for your business"
                features={[
                  "Workflow automation",
                  "Data processing tools",
                  "Business process optimizations",
                  "Custom quote based on requirements"
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="snap-start">
        <div className="bg-blue-500 text-white py-24">
          <div className="container-width text-center">
            <h2 className="text-3xl font-bold mb-6">Quotes are free, and so is good conversation. Let’s talk.</h2>
            <p className="text-xl mb-8">
For detailed pricing and custom quotes, feel free to reach out. We’re happy to explore the best solution for you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="space-y-4">
                <p className="text-lg">
                  Email:{' '}
                  <a href="mailto:contact@visrupt.in" className="underline hover:text-blue-100">
                    contact@visrupt.in
                  </a>
                </p>
                <p className="text-lg">
                  Call:{' '}
                  <a href="tel:+917522979568" className="underline hover:text-blue-100">
                    +91-7522979568
                  </a>
                </p>
              </div>
              <div className="sm:ml-8">
                <a
                  href="https://calendly.com/visrupt/schedule-a-call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-white text-blue-500 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 group"
                >
                  <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                  <span className="group-hover:translate-x-1 transition-transform duration-300">Schedule a Call</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </section>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 bg-blue-500 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

export default Pricing;